/**
 * Row label of an Azure Storage tree node (spec §5.1 badges): `[public: blob]`,
 * `[HNS]`, `[leased]`, `[deleted]`, an approximate queue count, a share quota,
 * `NFS` (S7); `$system` containers render greyed. Badges come from `StorageNodeMetadata`,
 * never from the name.
 */
import type { TreeNode } from '../../models/TreeModel';
import type { PinnedStorageNode, StorageActionNode, StorageNodeMetadata, StorageResourceNode, StorageServiceFolderNode } from '../../models/nodeTypes';
import { isStorageNodeType } from '../../models/azureStorageNodeFactories';

export type StorageLabelNode = StorageResourceNode | StorageServiceFolderNode | StorageActionNode | PinnedStorageNode;

export function isStorageLabelNode(node: TreeNode): node is StorageLabelNode {
  return isStorageNodeType(node.type) || node.type === 'pinned-storage';
}

export function storageNodeBadges(metadata: StorageNodeMetadata): string[] {
  const badges: string[] = [];
  if (metadata.publicAccess && metadata.publicAccess !== 'none') badges.push(`public: ${metadata.publicAccess}`);
  if (metadata.hns) badges.push('HNS');
  if (metadata.leased) badges.push('leased');
  if (metadata.deleted) badges.push('deleted');
  if (metadata.approximateMessageCount !== undefined) badges.push(`~${metadata.approximateMessageCount.toLocaleString()}`);
  if (metadata.quotaGiB !== undefined) badges.push(`${metadata.quotaGiB} GiB quota`);
  if (metadata.protocol === 'nfs') badges.push('NFS'); // S7: SMB is the default, unbadged
  if (metadata.availability === 'unverified') badges.push('not verified');
  if (metadata.dataAccessNote) badges.push(metadata.dataAccessNote.badge);
  return badges;
}

/** The row tooltip: the probe's data verdict (+ the Entra remedies when it names a role), or the unverified note. */
export function storageNodeTitle(metadata: StorageNodeMetadata): string | undefined {
  const note = metadata.dataAccessNote;
  if (note) {
    return note.role
      ? `${note.detail}. Assign the role in the Azure portal (Access control (IAM)), or switch the connection's data access to the account key.`
      : note.detail;
  }
  if (metadata.availabilityDetail) return metadata.availabilityDetail;
  if (metadata.fullPath) return metadata.fullPath;
  return metadata.availability === 'unverified' ? 'Not verified by Test Connection — the first listing is the test' : undefined;
}

/** A badge's own tooltip — the queue count is approximate (Azure's `approximateMessagesCount`). */
export function storageBadgeTitle(badge: string): string | undefined {
  const count = /^~([\d,.\s]+)$/u.exec(badge)?.[1];
  return count ? `About ${count} messages (Azure reports an approximate count)` : undefined;
}

/**
 * Round B U30: the name truncates with an ellipsis and the full name leads the tooltip; badges never shrink or wrap, so
 * a quota reads "1 GiB quota", not "1 GiB (". An action row ("Create container…") reads as an action: muted, with the
 * plus icon (treeNodeIconMap), never as an item.
 */
export function StorageNodeLabel({ node }: { node: StorageLabelNode }) {
  const badges = storageNodeBadges(node.metadata);
  const detail = storageNodeTitle(node.metadata);
  const isAction = node.type === 'storage-action';
  // The full name leads the tooltip unless the detail already names it (a single-file root's full path).
  const title = isAction ? detail : !detail ? node.name : detail.includes(node.name) ? detail : `${node.name}\n${detail}`;
  return (
    <span
      className={`flex min-w-0 items-center gap-1.5 ${node.metadata.system || isAction ? 'text-muted-foreground' : ''}`}
      title={title}
      data-testid={`storage-node-label-${node.type}`}
    >
      <span className="min-w-0 truncate">{node.name}</span>
      {badges.map((badge) => (
        <span key={badge} title={storageBadgeTitle(badge)} className="shrink-0 text-[10px] leading-4 px-1 rounded bg-muted text-muted-foreground font-normal whitespace-nowrap" data-testid="storage-node-badge">
          {badge}
        </span>
      ))}
    </span>
  );
}
